import { useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { getIntl } from '../../utils/intl';
import { getProfileCollaboratorInvoiceCacheKey, getProfileCollaboratorInvoicesCacheKey } from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import useFeatureToggles from './useFeatureToggles';

const intl = getIntl('useCollaboratorInvoices', {
    loadInvoicesError: 'Error loading collaborator invoices',
    loadInvoiceError: 'Error loading collaborator invoice',
});

export type UseCollaboratorInvoicesArgs = {
    medicalProfileId: string;
    collaboratorId: string;
    isOrganizationCollaborator: boolean;
    invoiceId?: string;
};

export type UseCollaboratorInvoicesReturnValue = {
    invoices: APITypes.organizationService.OrganizationInvoiceDetails[] | null;
    isLoadingInvoices: boolean;
    invoice: APITypes.medicalProfileService.GetOrganizationMedicalProfileCollaboratorInvoiceResponse | null;
    isLoadingInvoice: boolean;
    hasInvoices: boolean;
};

export const useCollaboratorInvoices = (args: UseCollaboratorInvoicesArgs): UseCollaboratorInvoicesReturnValue => {
    const hasInvoiceFeature = !!useFeatureToggles().featureToggles.organizationClientInvoicing;
    const { medicalProfileId, collaboratorId } = args;
    const invoicesQuery = useQuery({
        queryKey: getProfileCollaboratorInvoicesCacheKey(medicalProfileId, collaboratorId),
        queryFn: async () => {
            return appAPI.get<APITypes.medicalProfileService.ListOrganizationMedicalProfileCollaboratorInvoicesResponse>(
                urls.api.collaborators.collaboratorInvoices(medicalProfileId, collaboratorId),
            );
        },
        enabled: hasInvoiceFeature && args.isOrganizationCollaborator,
    });
    useQueryErrorNotify(invoicesQuery, intl('loadInvoicesError'));

    const invoiceQuery = useQuery({
        queryKey: getProfileCollaboratorInvoiceCacheKey(medicalProfileId, collaboratorId, args.invoiceId || ''),
        queryFn: async () => {
            return appAPI.get<APITypes.medicalProfileService.GetOrganizationMedicalProfileCollaboratorInvoiceResponse>(
                urls.api.collaborators.collaboratorInvoice(medicalProfileId, collaboratorId, args.invoiceId || ''),
            );
        },
        enabled: hasInvoiceFeature && !!args.invoiceId && args.isOrganizationCollaborator,
    });
    useQueryErrorNotify(invoiceQuery, intl('loadInvoiceError'));

    const hasInvoices =
        args.isOrganizationCollaborator && hasInvoiceFeature && (invoicesQuery.data?.items?.length || 0) > 0;

    return {
        invoices: invoicesQuery.data?.items || null,
        isLoadingInvoices: invoicesQuery.isLoading,
        invoice: invoiceQuery.data || null,
        isLoadingInvoice: invoiceQuery.isLoading,
        hasInvoices,
    };
};
